/* eslint-disable arrow-body-style */
import { RangePicker } from './RangePicker';
import React, { useCallback, useEffect, useMemo, useRef, useState, forwardRef, useImperativeHandle } from 'react';
import { Select, ConfigProvider, Bubble } from 'tea-component';
import './index.scss';
// import 方式
import moment, { Moment } from "moment";
import { shortcutOption, DateShortcutType, ShortcutOptionType } from './_config';
import { IAutoRange } from './CalendarTable';
import classNames from 'classnames';
moment.locale('en');

interface OptionsMapType {
  [type: string]: {
    value: string;
    text: string;
    text_cn?: string;
    /** 覆盖日历面板类型，如 lifetime 映射为 'date' */
    ttype?: string;
  };
}

interface IShotcutItemProps {
  item: DateShortcutType;
  selectedLeftType: string;
}

export interface IAutoRangeMap {
  [type: string]: IAutoRange;
}

// 组件对外暴露的方法接口
export interface IRangePickerRef {
  // 关闭弹出层
  close: () => void;
}

const typeDateList = ['1day', '3days', '7days', '30days', 'date', 'hour'];

const autoRangeMap: IAutoRangeMap = {
  '1day': {
    length: 0,
    autoPosition: 'head'
  },
  '3days': {
    length: 2,
    autoPosition: 'head'
  },
  '7days': {
    length: 6,
    autoPosition: 'head'
  },
  '30days': {
    length: 29,
    autoPosition: 'head'
  }
};

const localOptionsMap: OptionsMapType = {
  '1day': { value: "1day", text: "1 Day", text_cn: '1天' },
  '3days': { value: "3days", text: "3 Days", text_cn: '3天' },
  '7days': { value: "7days", text: "7 Days", text_cn: '7天' },
  '30days': { value: "30days", text: "30 Days", text_cn: '30天' },
  hour: { value: "hour", text: "Hour", text_cn: '时' },
  date: { value: "date", text: "Day", text_cn: '日' },
  week: { value: "week", text: "Week", text_cn: '周' },
  month: { value: "month", text: "Month", text_cn: '月' },
  year: { value: "year", text: "Year", text_cn: '年' },
  quarter: { value: 'quarter', text: 'Quarter', text_cn: '季度' },
};

const RangePickerComponent = forwardRef<IRangePickerRef, any>((props: any, ref) => {
  const {
    value = [moment(), moment()],
    defaultValue = {},
    type: typeProps,
    typeList = ['date'],
    disabled = false,
    timeTabDisabled = false,
    showSelectType = true,
    showShortcut = true,
    autoResetShortcutByValueChange = true,
    setShortcutOption,
    startOfWeek = 0,
    onChange,
    customOption = {},
    language,
    // 所有时间的起始时间，startOfTime和latestDate，不是所有地方都需要，需要从外部传入参数
    startOfTime = '',
    // 最新有数据的时间
    latestDate = {
      // date:moment().subtract(1, 'day'),
      // week: moment().weekday(-7),
      // month: moment().subtract(1,'month').startOf('month')
    },
    isReseted,
    showTips = false,
    tipsInfo = null,
    onTimeChange,
    // 额外的快捷选项，第二列展示
    extraShortcut,
    overlayClassName = '',
    typeSelectOverlayClassName = '',
    disabledDate = null,
    // 判断是否为未选择日期直接切换时间粒度类型
    setIsChangeDefaultType,
    // 返回日期组件选择的快捷键类型
    setTimeShortcutType,
    syncInput = true,
    // 是否展示快捷选项的name到筛选器标题
    showShortcutTypeTitle = false,
    // 默认选中的快捷选项
    defualtSelectedShortcut,
    // 是否是单选周
    singleWeek=false,
    optionsMap: externalOptionsMap,
    footerConfig,
    changeClose = true,
    selectClassName,
    customComponentClassName,
    rangePickerClassName,
    ...restProps
  } = props;
  const type = typeProps || 'date';
  const optionsMap = useMemo(() => ({
    ...localOptionsMap,
    ...externalOptionsMap,
  }), [externalOptionsMap]);
  const isCN = ['cn', 'zh_CN'].includes(language);

  const calendarInfoRef = useRef<any>({
    [type]: value
  });

  const childRef = useRef<any>();
  // const [calendarType, setCalendarType] = useState(type);
  // 左侧快捷选中项
  const [setSelectedShortcut, setSetSelectedShortcut] = useState(defualtSelectedShortcut || null);

  // 选择日期类型
  const handelChangeType = useCallback((type: string) => {
    // console.log('handelChangeType', type);
    // 防止单个日期时选中下拉的选项出现无效时间的问题
    if (typeList?.length <= 1) return;
    const value = calendarInfoRef.current?.[type];
    setSetSelectedShortcut(null);
    if (value && !isReseted) {
      onChange(value, type);
    } else {
      onChange(defaultValue?.[type] || [null, null], type);
    }
  }, [onChange, isReseted]);

  // 接管rangepicker 中的onchange
  const handleChangeValue = useCallback((value: Array<Moment | '' | null>, ...rest: any[]) => {
    setSetSelectedShortcut(null);
    if (setIsChangeDefaultType) setIsChangeDefaultType(type);
    calendarInfoRef.current[type] = value;
    onChange(value, type, ...rest);
  }, [onChange]);

  const options = typeList.map((val: string) => {
    const { value, text, text_cn } = optionsMap[val] || {};
    if (isCN) {
      return { value, text: text_cn };
    }
    return { value, text };
  });
  const calendarType: string = optionsMap?.[type]?.type || (typeDateList.includes(type) ? 'date' : type);
  const showTime: boolean = type === 'hour';
  const curDefaultValue = defaultValue?.[type];
  const curProps: any = customOption?.[type];
  // 从 customOption 中提取 customRender，剩余属性继续透传给 RangePicker
  const { customRender, ...curPropsRest } = curProps || {};

  // 左侧快捷选择状态设置
  const selectedLeftType = useMemo(() => {
    return setSelectedShortcut?.name || '';
  }, [setSelectedShortcut]);

  const ShotcutItem = useMemo(() => {
    return (props: IShotcutItemProps) => {
      const { item, selectedLeftType } = props;
      return <p
        className={`dtb-rangepicker-shortcut ${selectedLeftType === item?.name && 'dtb-rangepicker-shortcut-hover'}`}
        onClick={() => {
          handleChangeValue(item.onClick());
          setSetSelectedShortcut(item);
          setTimeShortcutType?.(item?.name);
          //  关闭时间组件弹出窗
          changeClose && childRef?.current?.setPopActive();
        }}>{isCN ? item.nameCN : item.name}</p>;
    };
  }, [isCN, handleChangeValue]);

  // 额外的快捷扩展
  const extraShortcutList = useMemo(() => {
    const extraShortcutList = extraShortcut?.[type] || [];
    if (!showShortcut || extraShortcutList.length < 1) {
      return null;
    }
    return extraShortcutList;
  }, [type, extraShortcut]);

  const shortcut = useMemo(() => {
    // 配置左侧栏快捷操作
    let shortcutList: DateShortcutType[] = [];
    if (showShortcut) {
      shortcutList = shortcutOption(
        startOfTime,
        latestDate,
      )?.[type] || [];
    }
    // 是否有自定义左侧栏快捷操作
    if (showShortcut && typeof setShortcutOption === 'function') {
      // 此处是浅拷贝，注意setShortcutOption不要修改参数
      const newShortcutOption: ShortcutOptionType = setShortcutOption({ ...shortcutOption(startOfTime, latestDate) });
      if(React.isValidElement(newShortcutOption)) {
        return React.Children.map(newShortcutOption, (child: any) => {
          return React.cloneElement(
            child,
            {
              handleChangeValue,
              setSelectLeftType: setSetSelectedShortcut,
              childRef,
              type,
              selectedLeftType
            }
          );
        });
      }
      shortcutList = newShortcutOption?.[type] || [];

    }
    if (shortcutList.length < 1) {
      return null;
    }

    if (!extraShortcutList) {
      return shortcutList.map((item: DateShortcutType) => {
        return <ShotcutItem key={item.name} item={item} selectedLeftType={selectedLeftType} />;
      });
    }

    return (<div className="db-shotcut-wrap">
      <div className="db-shotcut-column">
        {
          shortcutList.map((item: DateShortcutType) => {
            return <ShotcutItem key={item.name} item={item} selectedLeftType={selectedLeftType} />;
          })
        }
      </div>
      <div className="db-shotcut-column">
        {
          extraShortcutList.map((item: DateShortcutType) => {
            return <ShotcutItem key={item.name} item={item} selectedLeftType={selectedLeftType} />;
          })
        }
      </div>
    </div>);
  }, [type, extraShortcutList, setShortcutOption, selectedLeftType, ShotcutItem]);

  const rangePrefix = useMemo(() => {
    if (showSelectType) {
      return null;
    }
    const  { text, text_cn: textCn } = optionsMap[type] || {};
    let title = isCN ? textCn : text;
    if(showShortcutTypeTitle && showShortcut && selectedLeftType) {
      // setSelectedShortcut
      title = isCN ? setSelectedShortcut?.nameCN : setSelectedShortcut?.name;
    }
    return <div className={isCN ? 'dtb-rangepicker-label-prefix-cn' : 'dtb-rangepicker-label-prefix'}>{ title} </div>;
  }, [showSelectType, type, language, showShortcutTypeTitle, showShortcut, selectedLeftType]);

  const autoRange = useMemo(() => {
    return autoRangeMap?.[type];
  }, [type]);

  // 同步外部值
  useEffect(() => {
    if (calendarInfoRef.current[type] !== value) {
      if (showShortcut && autoResetShortcutByValueChange) {
        setSetSelectedShortcut(null);
      }
      calendarInfoRef.current[type] = value;
    }
  }, [value, type, showShortcut, autoResetShortcutByValueChange]);

  // 暴露方法给外界
  useImperativeHandle(ref, () => ({
    // 关闭弹出层
    close: () => {
      childRef?.current?.setPopActive?.();
    },
  }), [type, value, defaultValue, onChange, showShortcut, autoResetShortcutByValueChange]);

  return (
    <>
      {showSelectType &&
        <Bubble
          dark
          escapeWithReference
          flipped={false}
          destroyOnClose={false}
          content={showTips ? tipsInfo : null}
        >
          <Select
            appearance="button"
            options={options}
            disabled={timeTabDisabled || disabled || typeList?.length <= 1}
            value={type}
            onChange={value => handelChangeType(value)}
            className={classNames('dtb-datepicker-prefix', selectClassName)}
            overlayClassName={typeSelectOverlayClassName}
            matchButtonWidth={true}
          />
        </Bubble>
      }
      <ConfigProvider locale={language}>
        {customRender ? (
          customRender({
            value,
            onChange: handleChangeValue,
            disabled,
            className: customComponentClassName,
            ...curPropsRest,
          })
        ) : (
        <RangePicker
          rangePrefix={rangePrefix}
          key={type}
          autoRange={autoRange}
          fixDate={true}
          restoreView={true}
          syncInput={syncInput}
          singleWeek={singleWeek}
          {...restProps}
          disabled={disabled}
          {...curPropsRest}
          ref={childRef}
          showSelectType={showSelectType}
          header={shortcut}
          startOfWeek={startOfWeek}
          calendarType={calendarType}
          disabledDate={disabledDate?.[type]}
          value={value}
          defaultValue={curDefaultValue}
          showTime={showTime}
          onChange={handleChangeValue}
          onTimeChange={onTimeChange}
          className={classNames({
            'dtb-datepicker-width-auto': showShortcutTypeTitle && showShortcut,
          }, rangePickerClassName, restProps?.className)}
          overlayClassName={`dtb-rangepicker-overlay${extraShortcutList ? ' dtb-rangepicker-has-extra-shotcut' : ''}${overlayClassName ? ` ${overlayClassName}` : ''}`}
          footerConfig={footerConfig}
          changeClose={changeClose}
        />
        )}
      </ConfigProvider>
    </>
  );
});

// 设置组件显示名称
RangePickerComponent.displayName = 'RangePicker';

export default RangePickerComponent;
